/** Pure-SVG dual-speaker waveform. Deterministic pseudo-random for reproducibility. */

import type React from "react";

interface WaveformProps {
	height?: number;
	bars?: number;
	playhead?: number; // 0..1
	accent?: [string, string];
	compact?: boolean;
}

export function Waveform({
	height = 60,
	bars = 120,
	playhead = 0,
	accent = ["var(--color-primary)", "var(--color-success)"],
	compact = false,
}: WaveformProps) {
	// deterministic LCG pseudo-random
	let s = 1337;
	const rnd = () => {
		s = (s * 9301 + 49297) % 233280;
		return s / 233280;
	};

	const barEls: React.ReactNode[] = [];
	for (let i = 0; i < bars; i++) {
		const speaker = rnd() > 0.5 ? 0 : 1;
		const base = 0.25 + rnd() * 0.75;
		const env = 0.5 + 0.5 * Math.sin((i / bars) * Math.PI * 3 + speaker);
		const amp = Math.min(1, base * env);
		const h = Math.max(2, amp * (height - 4));
		const x = i * 4 + 1;
		const y = (height - h) / 2;
		barEls.push(
			<rect
				key={i}
				x={x}
				y={y}
				width={2}
				height={h}
				rx={1}
				fill={accent[speaker]}
				opacity={0.85}
			/>,
		);
	}

	const playheadX = bars * 4 * playhead;

	return (
		<div className="relative w-full">
			<svg
				viewBox={`0 0 ${bars * 4} ${height}`}
				preserveAspectRatio="none"
				className="w-full"
				aria-hidden="true"
				style={{ height }}
			>
				{barEls}
				<line
					x1={playheadX}
					y1={0}
					x2={playheadX}
					y2={height}
					stroke="var(--color-ink)"
					strokeWidth={1.5}
				/>
				<circle cx={playheadX} cy={height - 2} r={3} fill="var(--color-ink)" />
			</svg>
			{!compact && (
				<div className="mt-1 flex justify-between font-mono text-[10px] text-ink-tertiary">
					<span>0:00</span>
					<span>8:52</span>
					<span>17:44</span>
					<span>26:36</span>
					<span>35:28</span>
				</div>
			)}
		</div>
	);
}
